import React, { useEffect, useState } from "react";
import styled, { withTheme } from "styled-components";
import { nameRegex, regex } from "../../../modules/constants";
import { PasswordInput } from "../../ui-library/AuthControls/PasswordInput";
import { Button } from "../../ui-library/AuthControls/Button";
import TermsCheckbox from "../../ui-library/AuthControls/TermsCheckbox";
import Alert from "../../ui-library/Alert/Alert";
import PwChangeSuccessIcon from "../../../assets/svg/pw_change_success.svg";
import AlertUGNX from "../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../ToastsAndAlerts/constants";
import LoadingSpinner from "../../ui-library/LoadingSpinner/LoadingSpinner";
import AutoFocusInput from "../../ui-library/AutoFocusInput/AutoFocusInput";
import { useTranslation } from "react-i18next";

const StyledInput = styled.input`
  border-bottom: 2px solid ${(props) => props.theme?.darkPurple} !important;
  &.invalid {
    border-bottom: 2px solid ${(props) => props.theme?.cardinal} !important;
  }
`;

const StyledAutoFocusInput = styled(AutoFocusInput)`
  border-bottom: 2px solid ${(props) => props.theme?.darkPurple} !important;
  &.invalid {
    border-bottom: 2px solid ${(props) => props.theme?.cardinal} !important;
  }
`;

const StyledError = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${(props) => props.theme?.cardinal};
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  font-weight: 600;
  color: ${(props) => props.theme?.nero};
  opacity: 0.63;
`;

const SuccessDiv = styled.div`
  height: 100%;
`;

const AdminRegistration = (props) => {
  const {
    isTokenValid,
    submittedChange,
    showLoginModal,
    closeModal,
    handleRegister,
    errorMessage,
    token,
    email,
  } = props;

  const { t } = useTranslation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [validPassword, setValidPassword] = useState(false);
  const [validName, setValidName] = useState(false);
  const [validConfirmPassword, setValidConfirmPassword] = useState(false);

  const handleFirstName = (event) => {
    setFirstName(event.target.value);
  };

  const handleLastName = (event) => {
    setLastName(event.target.value);
  };

  const onCheckboxChange = (value) => {
    setAcceptedTerms(value);
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const onConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleRegister(token, email, firstName, lastName, password, acceptedTerms);
  };

  const goHome = (e) => {
    e.preventDefault();
    closeModal();
  };

  useEffect(() => {
    if ((password && !regex.test(password)) || !password) {
      setValidPassword(false);
    } else {
      setValidPassword(true);
    }
  }, [password]);

  useEffect(() => {
    const name = firstName + lastName;
    if (name && nameRegex.test(name)) {
      setValidName(false);
    } else {
      setValidName(true);
    }
  }, [firstName, lastName]);

  useEffect(() => {
    if ((confirmPassword && !regex.test(confirmPassword)) || !confirmPassword) {
      setValidConfirmPassword(false);
    } else {
      setValidConfirmPassword(true);
    }
  }, [confirmPassword]);

  return !(isTokenValid.length <= 0) ? (
    isTokenValid === true ? (
      submittedChange ? (
        <SuccessDiv data-testid="success-modal">
          <Alert
            callback={showLoginModal}
            icon={PwChangeSuccessIcon}
            btnText={t("appText.auth.setup.account.success.action")}
            message={t("appText.auth.setup.account.success")}
          />
        </SuccessDiv>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="auth-form"
          data-testid="admin-registration-view"
        >
          <Subtitle>{t("appText.auth.setup.account.description")}</Subtitle>
          <StyledAutoFocusInput
            data-testid="first-name"
            type="text"
            className="form-control mb-3"
            placeholder={t("appText.auth.setup.account.firstName")}
            value={firstName}
            onChange={handleFirstName}
            maxLength="30"
          />
          <StyledInput
            data-testid="last-name"
            type="text"
            className="form-control"
            placeholder={t("appText.auth.setup.account.lastName")}
            value={lastName}
            onChange={handleLastName}
            maxLength="30"
          />
          {!validName && (
            <StyledError data-testid="name-validation-error">
              {t("errorText.auth.invalid.name")}
            </StyledError>
          )}
          <PasswordInput
            placeholder={t("appText.auth.setup.account.password")}
            onChange={onPasswordChange}
            value={password}
            field="1"
            inputError={password !== confirmPassword}
            margin="mb-3 mt-3"
          />
          <PasswordInput
            placeholder={t("appText.auth.setup.account.confirmPassword")}
            onChange={onConfirmPasswordChange}
            value={confirmPassword}
            field="2"
            inputError={password !== confirmPassword}
          />
          {password && (
            <StyledError data-testid="validation-error">
              {!validPassword && (
                <span className="invalid-input">
                  {t("errorText.auth.setup.account.password.validation")}
                </span>
              )}
              {password !== confirmPassword && validPassword && (
                <span>
                  {t("errorText.auth.setup.account.password.notMatch")}
                </span>
              )}
            </StyledError>
          )}
          <TermsCheckbox
            data-testid="terms-conditions"
            onChange={onCheckboxChange}
            text={t("appText.auth.setup.privacyPolicy.label", {
              link: "https://www.ultragenyx.com/privacy-policy/",
            })}
          />
          {errorMessage && (
            <AlertUGNX type={AlertType.DANGER}>{errorMessage}</AlertUGNX>
          )}
          <Button
            disabled={
              !(
                firstName &&
                lastName &&
                validName &&
                validPassword &&
                validConfirmPassword &&
                acceptedTerms &&
                password === confirmPassword
              )
            }
          >
            {t("appText.auth.setup.account.submit")}
          </Button>
        </form>
      )
    ) : (
      <form onSubmit={goHome}>
        <p>The link has expired.</p>
        <Button text="Home" />
      </form>
    )
  ) : (
    <LoadingSpinner />
  );
};

export default withTheme(AdminRegistration);
